import React, { Suspense, useEffect } from 'react'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'

import { captureError } from '@/app/service/util/error'

export type TRoute = {
  name: string
  path: string
  exact?: boolean
  component: React.ComponentType<any>
}

type WrapperProps = {
  routes: TRoute[]
  Loader: React.ComponentType
  ErrorPage: React.ComponentType
}

const Wrapper: React.FC<WrapperProps> = ({ routes, Loader, ErrorPage }) => {
  const location = useLocation()
  const history = useHistory()

  // biome-ignore lint/correctness/useExhaustiveDependencies: Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <ErrorBoundary ErrorPage={ErrorPage}>
      <Suspense fallback={<Loader />}>
        <Switch>
          {routes.map(({ name, path, exact, component: Page }) => (
            <Route
              key={name}
              path={path}
              exact={exact}
              location={location}
              render={(props: any) => (
                <ErrorBoundary ErrorPage={ErrorPage}>
                  <Page
                    name={name}
                    history={history}
                    location={location}
                    match={{
                      ...props.match,
                      url: `${props.match.url}${location.search}`,
                    }}
                  />
                </ErrorBoundary>
              )}
            />
          ))}
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}

// Custom ErrorBoundary component since React doesn't have a hook for error boundaries
class ErrorBoundary extends React.PureComponent<
  {
    ErrorPage: React.ComponentType
    children: React.ReactNode
  },
  { hasError: boolean }
> {
  state = {
    hasError: false,
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    captureError(error, { componentStack: errorInfo.componentStack })
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  render() {
    const { children, ErrorPage } = this.props

    if (this.state.hasError) {
      return <ErrorPage />
    }

    return children
  }
}

export default Wrapper
